import React from 'react';
import { useDispatch, useSelector } from 'react-redux';
import theme from '../theme';
import {
	Box,
	HStack,
	VStack,
	Button,
	Separator,
	Heading,
	Spacer,
	Text,
	Flex,
	Textarea,
	Input,
	Field,
	CloseButton,
	Accordion,
	NativeSelect,
	Portal,
	Dialog
} from '@chakra-ui/react';
import {
	hydrateInspectionForm,
	updateTitle,
	updateInfoField,
	updateObservationsField,
	updateUnsafeActsProcedureDeviationsField,
	updateAdditionalActProcedure,
	updateComments,
	updateViolationsField
} from '../store/safetyDatabaseInspection/safetyDatabaseInspection.actions';
import moment from 'moment';
import DatePicker from 'react-datepicker';
import { saveSafetyInspection } from '../lib/safetyForms';
import Autocomplete from '../components/Autocomplete';
import { FaArrowLeft } from 'react-icons/fa';
import { INITIAL_FORM_STATE } from '../store/safetyInspection/safetyInspection.reducer';

import 'react-datepicker/dist/react-datepicker.css';

const MAJOR_MODERATE_MINOR_SELECTIONS = ['Major', 'Moderate', 'Minor'];

const infoReqs = {
	completed_by: true,
	date: true,
	project_location: true
};

export default ({ id, submission, handleExport }) => {
	const dispatch = useDispatch();

	// On submitted forms, we fall back to INITIAL_FORM_STATE until we hydrate the form in the useEffect
	const { info, observations, unsafe_acts_procedure_deviations, comments, violations } = useSelector(
		state => state['safetyDatabaseInspection']?.[id]?.formData || submission || INITIAL_FORM_STATE
	);

	const [removeAutocompleteDialogOpen, setRemoveAutocompleteDialogOpen] = React.useState(false);
	const [editMode, setEditMode] = React.useState(false);
	const [confirmChanges, setConfirmChanges] = React.useState(false);
	const [errorDialog, setErrorDialog] = React.useState(null);

	const confirmChangesRef = React.useRef();
	const errorModalRef = React.useRef();

	React.useEffect(() => {
		if (submission) {
			dispatch(
				hydrateInspectionForm({
					id,
					submission
				})
			);
		}
	}, []);

	const employeeList = React.useMemo(
		() => window.employeeList.map(({ name }) => ({ employee: name })),
		[window.employeeList]
	);

	const projectsList = React.useMemo(
		() =>
			window.projectList.map(project => ({
				project: `${project.phase_name} - ${project.project_name}`,
				projectNumber: `${project.project_number}-${project.phase_number}`
			})),
		[window.projectList]
	);

	const observationsSafetyReqMet = React.useMemo(
		() => Object.values(observations.safety.fields).every(({ na, m, n }) => na || m || n),
		[observations.safety]
	);

	const observationsSupervisorReqMet = React.useMemo(
		() => Object.values(observations.supervisor_performance.fields).every(({ na, m, n }) => na || m || n),
		[observations.supervisor_performance]
	);

	const observationsOperationsReqMet = React.useMemo(
		() => Object.values(observations.operations.fields).every(({ na, m, n }) => na || m || n),
		[observations.operations]
	);

	const observationsHumanEnvironmentalReqsMet = React.useMemo(
		() => Object.values(observations.human_environmental_factors.fields).every(({ na, m, n }) => na || m || n),
		[observations.human_environmental_factors]
	);

	const observationsReqsMet = React.useMemo(
		() => ({
			safety: observationsSafetyReqMet,
			supervisor_performance: observationsSupervisorReqMet,
			operations: observationsOperationsReqMet,
			human_environmental_factors: observationsHumanEnvironmentalReqsMet
		}),
		[
			observationsSafetyReqMet,
			observationsSupervisorReqMet,
			observationsOperationsReqMet,
			observationsHumanEnvironmentalReqsMet
		]
	);

	return (
		<Box px={4} pt={4} pb={100}>
			{/*Alert Dialogs*/}
			<Dialog.Root
				role="alertdialog"
				closeOnEscape={false}
				closeOnInteractOutside={false}
				onOpenChange={({ open }) => {
					if (!open) {
						setRemoveAutocompleteDialogOpen(false);
					}
				}}
				open={Boolean(removeAutocompleteDialogOpen)}
				placement="center"
			>
				<Portal>
					<Dialog.Backdrop />
					<Dialog.Positioner>
						<Dialog.Content opacity="1 !important">
							<Dialog.Header>
								<Dialog.Title fontSize="lg" fontWeight="bold">
									Could not find an employee match.
								</Dialog.Title>
							</Dialog.Header>
							<Dialog.Body>
								<p>
									Please check that the name is spelled correctly. We suggest you select a match from
									the autocomplete list below the input. The employee will be removed, please try
									adding again with a correct selection.
								</p>
							</Dialog.Body>
							<Dialog.Footer>
								<Button
									ml={3}
									bg={theme.colors.red[500]}
									_hover={{ background: theme.colors.red[600] }}
									color={theme.colors.white}
									size="lg"
									onClick={() => {
										setRemoveAutocompleteDialogOpen(false);
										dispatch(updateInfoField({ id, key: removeAutocompleteDialogOpen, value: '' }));

										if (removeAutocompleteDialogOpen === 'completed_by') {
											dispatch(
												updateTitle({
													id,
													completedBy: '',
													projectNumber: info.project_number.value,
													date: moment(info.date.value * 1000).format('MM-DD-YY')
												})
											);
										}
									}}
								>
									Remove Item
								</Button>
							</Dialog.Footer>
						</Dialog.Content>
					</Dialog.Positioner>
				</Portal>
			</Dialog.Root>
			<Dialog.Root
				role="alertdialog"
				initialFocusEl={confirmChangesRef}
				onOpenChange={({ open }) => {
					if (!open) {
						setConfirmChanges(false);
					}
				}}
				open={confirmChanges}
				placement="center"
			>
				<Portal>
					<Dialog.Backdrop />
					<Dialog.Positioner>
						<Dialog.Content opacity="1 !important">
							<Dialog.Header>
								<Dialog.Title fontSize="lg" fontWeight="bold">
									Confirm Changes
								</Dialog.Title>
							</Dialog.Header>
							<Dialog.Body>
								<p>Are you sure you want to save your changes?</p>
							</Dialog.Body>
							<Dialog.Footer>
								<Dialog.ActionTrigger asChild>
									<Button
										ref={confirmChangesRef}
										color={theme.colors.white}
										bg={theme.colors.red[500]}
										_hover={{ background: theme.colors.red[600] }}
										onClick={() => {
											setConfirmChanges(false);
											setEditMode(false);

											dispatch(hydrateInspectionForm({ id, submission }));
										}}
									>
										No
									</Button>
								</Dialog.ActionTrigger>
								<Button
									colorPalette="red"
									ml={3}
									color={theme.colors.white}
									bg={theme.colors.blue[700]}
									_hover={{ backgroundColor: theme.colors.blue[800] }}
									onClick={async () => {
										setConfirmChanges(false);

										await saveSafetyInspection(id, {
											...info,
											date: {
												...info.date,
												value: moment(info.date.value * 1000).format('YYYY-MM-DD')
											},
											observations,
											unsafe_acts_procedure_deviations,
											comments,
											violations: violations.map(({ description, majorModerateMinor }) => ({
												description,
												majorModerateMinor: majorModerateMinor.toLowerCase()
											}))
										})
											.then(() => setEditMode(false))
											.catch(error => {
												setErrorDialog({
													header: 'Error Saving Changes',
													body: error.message
												});
											});
									}}
								>
									Yes
								</Button>
							</Dialog.Footer>
							<Dialog.CloseTrigger asChild>
								<CloseButton size="sm" />
							</Dialog.CloseTrigger>
						</Dialog.Content>
					</Dialog.Positioner>
				</Portal>
			</Dialog.Root>
			<Dialog.Root
				role="alertdialog"
				initialFocusEl={errorModalRef}
				onOpenChange={({ open }) => {
					if (!open) {
						setErrorDialog(null);
					}
				}}
				open={Boolean(errorDialog)}
				placement="center"
			>
				<Portal>
					<Dialog.Backdrop />
					<Dialog.Positioner>
						<Dialog.Content opacity="1 !important">
							<Dialog.Header>
								<Dialog.Title fontSize="lg" fontWeight="bold">
									{errorDialog?.header}
								</Dialog.Title>
							</Dialog.Header>
							<Dialog.Body>
								<p>{errorDialog?.body}</p>
							</Dialog.Body>
							<Dialog.Footer>
								<Dialog.ActionTrigger asChild>
									<Button
										ref={errorModalRef}
										ml={3}
										color={theme.colors.white}
										bg={theme.colors.blue[700]}
										_hover={{ backgroundColor: theme.colors.blue[800] }}
										onClick={() => setErrorDialog(null)}
									>
										Okay
									</Button>
								</Dialog.ActionTrigger>
							</Dialog.Footer>
							<Dialog.CloseTrigger asChild>
								<CloseButton size="sm" />
							</Dialog.CloseTrigger>
						</Dialog.Content>
					</Dialog.Positioner>
				</Portal>
			</Dialog.Root>
			{/*Header*/}
			<HStack spacing={2} justify="flex-end" p={2}>
				<Button
					size="lg"
					color={theme.colors.white}
					bg={theme.colors.blue[700]}
					_hover={{ backgroundColor: theme.colors.blue[800] }}
					onClick={() => window.open(window.location.origin + '/dashboard/safety-database/', '_self')}
					borderRadius="4px"
					p="5px 5px 5px 8px"
				>
					<FaArrowLeft w="30px" h="30px" />
				</Button>
				<VStack spacing="8px" mb="24px !important" align="flex-start">
					<Heading size="lg" pt="24px" color={theme.colors.blue[700]}>
						Safety Inspection Form
					</Heading>
				</VStack>
				<Spacer />
				<Button
					size="lg"
					color={theme.colors.white}
					bg={theme.colors.blue[700]}
					_hover={{ backgroundColor: theme.colors.blue[800] }}
					onClick={() => handleExport([submission.sid])}
				>
					Export
				</Button>
				{window.userRoles.includes('administrator') && (
					<Button
						size="lg"
						color={editMode ? theme.colors.black : theme.colors.white}
						bg={editMode ? 'gray.100' : theme.colors.blue[700]}
						_hover={{ backgroundColor: editMode ? 'gray.200' : theme.colors.blue[800] }}
						variant={editMode ? 'ghost' : 'solid'}
						onClick={() => {
							if (editMode) {
								setConfirmChanges(true);
							} else {
								setEditMode(editMode => !editMode);
							}
						}}
					>
						Edit Form
					</Button>
				)}
			</HStack>
			<Separator />
			{/*Safety Inspection Form*/}
			<Flex flexDirection="row" alignItems="center" justifyContent="space-between" wrap="wrap">
				{Object.keys(info).map((key, index) => (
					<Box
						key={`${key}_${index}`}
						flex={1}
						flexBasis="48%"
						p="8px"
						m="8px"
						bgColor={theme.colors.gray[500]}
						borderRadius="base"
						position="relative"
					>
						<Text fontSize={16} as="label" pb="4px">
							{info[key].title}
						</Text>
						{key === 'project_description_activity' ? (
							<Textarea
								fontSize={16}
								disabled={Boolean(submission)}
								_disabled={{
									opacity: 1,
									bgColor: theme.colors.gray[400],
									color: theme.colors.black
								}}
								bgColor={theme.colors.white}
								borderRadius="base"
								value={info[key].value}
								onChange={({ target }) => dispatch(updateInfoField({ id, key, value: target.value }))}
							/>
						) : key === 'date' ? (
							<>
								<Box _hover={{ cursor: submission ? 'default' : 'pointer' }}>
									<DatePicker
										disabled={Boolean(submission)}
										selected={info.date.value * 1000}
										onChange={date => {
											const timestamp = Math.floor(moment(date).valueOf() / 1000);

											dispatch(updateInfoField({ id, key, value: timestamp }));
											dispatch(
												updateTitle({
													id,
													completedBy: info.completed_by.value,
													projectNumber: info.project_number.value,
													date: moment(timestamp * 1000).format('MM-DD-YY')
												})
											);
										}}
										customInput={
											<Input fontSize={16} bgColor={theme.colors.white} borderRadius="base" />
										}
									/>
								</Box>
							</>
						) : (
							<Field.Root invalid={infoReqs[key] && !info[key].value}>
								<Autocomplete
									disabled={Boolean(submission) && (!editMode || key !== 'completed_by')}
									onBlur={() =>
										setRemoveAutocompleteDialogOpen(
											(key === 'completed_by' ||
												key === 'project_manager' ||
												key === 'supervisor') &&
												info[key].value &&
												!employeeList.find(({ employee }) => employee === info[key].value)
												? key
												: false
										)
									}
									dataList={
										key === 'project_location' || key === 'project_number'
											? projectsList
											: employeeList
									}
									fieldName={
										key === 'project_location'
											? 'project'
											: key === 'project_number'
												? 'projectNumber'
												: 'employee'
									}
									value={info[key].value}
									onChange={value => {
										dispatch(updateInfoField({ id, key, value }));

										if (key === 'completed_by') {
											dispatch(
												updateTitle({
													id,
													completedBy: value,
													projectNumber: info.project_number.value,
													date: moment(info.date.value * 1000).format('MM-DD-YY')
												})
											);
										} else if (key === 'project_number') {
											dispatch(
												updateTitle({
													id,
													completedBy: info.completed_by.value,
													projectNumber: value,
													date: moment(info.date.value * 1000).format('MM-DD-YY')
												})
											);
										}
									}}
									onSelected={value => {
										dispatch(updateInfoField({ id, key, value }));

										if (key === 'completed_by') {
											dispatch(
												updateTitle({
													id,
													completedBy: value,
													projectNumber: info.project_number.value,
													date: moment(info.date.value * 1000).format('MM-DD-YY')
												})
											);
										} else if (key === 'project_number') {
											dispatch(
												updateInfoField({
													id,
													key: 'project_location',
													value:
														projectsList.find(
															({ projectNumber }) => projectNumber === value
														)?.project || info.project_location.value
												})
											);

											dispatch(
												updateTitle({
													id,
													completedBy: info.completed_by.value,
													projectNumber: value,
													date: moment(info.date.value * 1000).format('MM-DD-YY')
												})
											);
										} else if (key === 'project_location') {
											const projectNumber =
												projectsList.find(({ project }) => project === value)?.projectNumber ||
												info.project_number.value;

											dispatch(
												updateInfoField({
													id,
													key: 'project_number',
													value: projectNumber
												})
											);

											dispatch(
												updateTitle({
													id,
													completedBy: info.completed_by.value,
													projectNumber,
													date: moment(info.date.value * 1000).format('MM-DD-YY')
												})
											);
										}
									}}
								/>
							</Field.Root>
						)}
					</Box>
				))}
			</Flex>
			<Accordion.Root multiple collapsible p="8px" defaultValue={[0, 1, 2, 3]}>
				<Accordion.Item value={0} mb={4} borderBottomWidth="1px">
					<Accordion.ItemTrigger>
						<Accordion.ItemIndicator />
						<Text fontSize={20} fontWeight="bold" color={theme.colors.blue[700]}>
							Observations
						</Text>
					</Accordion.ItemTrigger>
					<Accordion.ItemContent p={0}>
						<Accordion.ItemBody>
							<Flex flexDirection="row" alignItems="center" justifyContent="space-between" wrap="wrap">
								{Object.keys(observations).map((observationSection, index) => (
									<Box
										key={`${observationSection}_${index}`}
										flex={1}
										flexBasis="48%"
										p="8px"
										m="8px"
										bgColor={theme.colors.gray[500]}
										borderRadius="base"
										borderBottom={`2px solid ${
											!observationsReqsMet[observationSection] ? 'red' : 'transparent'
										}`}
									>
										<HStack pb="8px">
											<Text
												fontSize={16}
												flexGrow={0}
												flexShrink={1}
												flexBasis="75%"
												as="label"
												pb="4px"
											>
												{observations[observationSection].title}
											</Text>
											<Flex
												flexGrow={0}
												flexShrink={1}
												flexBasis="25%"
												ml="2px"
												alignItems="center"
												justifyContent="space-between"
											>
												<Text fontSize={16} whiteSpace="nowrap" w="20px" textAlign="center">
													NA
												</Text>
												<Text fontSize={16} whiteSpace="nowrap" w="20px" textAlign="center">
													M
												</Text>
												<Text fontSize={16} whiteSpace="nowrap" w="20px" textAlign="center">
													N
												</Text>
											</Flex>
										</HStack>
										{Object.keys(observations[observationSection].fields).map(
											(observation, _index) => (
												<Flex
													key={`${observation}_${_index}`}
													flexDirection="row"
													alignItems="center"
													justifyContent="space-between"
													mb="10px"
												>
													<Text fontSize={16} flexGrow={0} flexShrink={1} flexBasis="75%">
														{observations[observationSection].fields[observation].title}
													</Text>
													<Flex
														flexGrow={1}
														flexShrink={1}
														flexBasis="25%"
														justifyContent="space-between"
														ml="2px"
													>
														<Box
															w="20px"
															h="20px"
															borderWidth="2px"
															borderRadius="50%"
															borderColor={theme.colors.blue[700]}
															bgColor={
																observations[observationSection].fields[observation].na
																	? theme.colors.blue[700]
																	: 'transparent'
															}
															_hover={{ cursor: submission ? 'default' : 'pointer' }}
															pointerEvents={submission ? 'none' : 'auto'}
															onClick={() => {
																if (!submission) {
																	dispatch(
																		updateObservationsField({
																			id,
																			section: observationSection,
																			observation,
																			key: 'na',
																			value: !observations[observationSection]
																				.fields[observation].na
																		})
																	);
																}
															}}
														/>
														<Box
															w="20px"
															h="20px"
															borderWidth="2px"
															borderRadius="50%"
															borderColor={theme.colors.blue[700]}
															bgColor={
																observations[observationSection].fields[observation].m
																	? theme.colors.blue[700]
																	: 'transparent'
															}
															_hover={{ cursor: submission ? 'default' : 'pointer' }}
															pointerEvents={submission ? 'none' : 'auto'}
															onClick={() => {
																if (!submission) {
																	dispatch(
																		updateObservationsField({
																			id,
																			section: observationSection,
																			observation,
																			key: 'm',
																			value: !observations[observationSection]
																				.fields[observation].m
																		})
																	);
																}
															}}
														/>
														<Box
															w="20px"
															h="20px"
															borderWidth="2px"
															borderRadius="50%"
															borderColor={theme.colors.blue[700]}
															bgColor={
																observations[observationSection].fields[observation].n
																	? theme.colors.blue[700]
																	: 'transparent'
															}
															_hover={{ cursor: submission ? 'default' : 'pointer' }}
															pointerEvents={submission ? 'none' : 'auto'}
															onClick={() => {
																if (!submission) {
																	dispatch(
																		updateObservationsField({
																			id,
																			section: observationSection,
																			observation,
																			key: 'n',
																			value: !observations[observationSection]
																				.fields[observation].n
																		})
																	);
																}
															}}
														/>
													</Flex>
												</Flex>
											)
										)}
									</Box>
								))}
							</Flex>
						</Accordion.ItemBody>
					</Accordion.ItemContent>
				</Accordion.Item>
				<Accordion.Item value={1} mb={4} borderBottomWidth="1px">
					<Accordion.ItemTrigger>
						<Accordion.ItemIndicator />
						<Text fontSize={20} fontWeight="bold" color={theme.colors.blue[700]}>
							Unsafe Acts / Procedure Deviations
						</Text>
					</Accordion.ItemTrigger>
					<Accordion.ItemContent p={0}>
						<Accordion.ItemBody>
							<Flex flexDirection="row" alignItems="center" justifyContent="flex-start" wrap="wrap">
								{Object.keys(unsafe_acts_procedure_deviations.fields).map((deviation, index) => (
									<HStack
										key={`${deviation}_${index}`}
										minW="23.5%"
										maxW="23.5%"
										flex={1}
										flexBasis="23%"
										p="8px"
										m="8px"
										bgColor={theme.colors.gray[500]}
										borderRadius="base"
										borderBottom={`2px solid ${
											!(
												unsafe_acts_procedure_deviations.fields[deviation].yes ||
												unsafe_acts_procedure_deviations.fields[deviation].no
											)
												? 'red'
												: 'transparent'
										}`}
									>
										<Flex flexDirection="row" alignItems="center">
											<Flex px="8px" justifyContent="center" alignItems="center">
												<Flex
													justifyContent="center"
													alignItems="center"
													w="24px"
													h="24px"
													borderWidth="2px"
													borderRadius="50%"
													borderColor={
														unsafe_acts_procedure_deviations.fields[deviation].yes
															? 'red'
															: 'transparent'
													}
													_hover={{ cursor: submission ? 'default' : 'pointer' }}
													pointerEvents={submission ? 'none' : 'auto'}
													onClick={() => {
														if (!submission) {
															dispatch(
																updateUnsafeActsProcedureDeviationsField({
																	id,
																	deviation,
																	key: 'yes',
																	value: !unsafe_acts_procedure_deviations.fields[
																		deviation
																	].yes
																})
															);
														}
													}}
												>
													<Text fontSize={18} w="17.5px" align="center">
														Y
													</Text>
												</Flex>
											</Flex>
											<Flex px="8px" justifyContent="center" alignItems="center">
												<Flex
													justifyContent="center"
													alignItems="center"
													w="24px"
													h="24px"
													borderWidth="2px"
													borderRadius="50%"
													borderColor={
														unsafe_acts_procedure_deviations.fields[deviation].no
															? 'green'
															: 'transparent'
													}
													_hover={{ cursor: submission ? 'default' : 'pointer' }}
													pointerEvents={submission ? 'none' : 'auto'}
													onClick={() => {
														if (!submission) {
															dispatch(
																updateUnsafeActsProcedureDeviationsField({
																	id,
																	deviation,
																	key: 'no',
																	value: !unsafe_acts_procedure_deviations.fields[
																		deviation
																	].no
																})
															);
														}
													}}
												>
													<Text fontSize={18} w="17.5px" align="center">
														N
													</Text>
												</Flex>
											</Flex>
										</Flex>
										<Text fontSize={16} as="label" pl="4px">
											{unsafe_acts_procedure_deviations.fields[deviation].title}
										</Text>
									</HStack>
								))}
								{unsafe_acts_procedure_deviations.additional_act_procedure.map(({ title }, index) => (
									<Box
										key={`${title}_${index}`}
										minW="23.5%"
										maxW="23.5%"
										flex={1}
										flexBasis="23%"
										p="8px"
										m="8px"
										bgColor={theme.colors.gray[500]}
										borderRadius="base"
										borderBottom={`2px solid ${
											!(
												unsafe_acts_procedure_deviations.additional_act_procedure[index].yes ||
												unsafe_acts_procedure_deviations.additional_act_procedure[index].no
											)
												? 'red'
												: 'transparent'
										}`}
									>
										<Flex flexDirection="row" alignItems="center" h="31px">
											<Box flex={5}>
												<Text fontSize={16} as="label" pb="4px">
													{title}
												</Text>
											</Box>
											<Flex flex={1} ml="2px" justifyContent="center" alignItems="center">
												<Flex
													justifyContent="center"
													alignItems="center"
													w="20px"
													h="20px"
													borderWidth="2px"
													borderRadius="50%"
													borderColor={
														unsafe_acts_procedure_deviations.additional_act_procedure[index]
															.yes
															? 'red'
															: 'transparent'
													}
													_hover={{ cursor: submission ? 'default' : 'pointer' }}
													pointerEvents={submission ? 'none' : 'auto'}
													onClick={() => {
														if (!submission) {
															dispatch(
																updateAdditionalActProcedure({
																	id,
																	index,
																	key: 'yes',
																	value: !unsafe_acts_procedure_deviations
																		.additional_act_procedure[index].yes
																})
															);
														}
													}}
												>
													<Text fontSize={16} w="17.5px" align="center">
														Y
													</Text>
												</Flex>
											</Flex>
											<Flex flex={1} ml="2px" justifyContent="center" alignItems="center">
												<Flex
													justifyContent="center"
													alignItems="center"
													w="20px"
													h="20px"
													borderWidth="2px"
													borderRadius="50%"
													borderColor={
														unsafe_acts_procedure_deviations.additional_act_procedure[index]
															.no
															? 'green'
															: 'transparent'
													}
													_hover={{ cursor: submission ? 'default' : 'pointer' }}
													pointerEvents={submission ? 'none' : 'auto'}
													onClick={() => {
														if (!submission) {
															dispatch(
																updateAdditionalActProcedure({
																	id,
																	index,
																	key: 'no',
																	value: !unsafe_acts_procedure_deviations
																		.additional_act_procedure[index].no
																})
															);
														}
													}}
												>
													<Text fontSize={16} w="17.5px" align="center">
														N
													</Text>
												</Flex>
											</Flex>
										</Flex>
									</Box>
								))}
							</Flex>
						</Accordion.ItemBody>
					</Accordion.ItemContent>
				</Accordion.Item>
				<Accordion.Item value={2} mb={4} borderBottomWidth="1px">
					<Accordion.ItemTrigger>
						<Accordion.ItemIndicator />
						<Text fontSize={20} fontWeight="bold" color={theme.colors.blue[700]}>
							Comments
						</Text>
					</Accordion.ItemTrigger>
					<Accordion.ItemContent p={0}>
						<Accordion.ItemBody>
							<Flex flexDirection="row" alignItems="center" justifyContent="flex-start" wrap="wrap">
								<Box
									flex={1}
									flexBasis="48%"
									p="8px"
									m="8px"
									bgColor={theme.colors.gray[500]}
									borderRadius="base"
								>
									<Text fontSize={16} as="label" pb="4px">
										Comments (Good, Practices, Non-Compliant Behaviors, Actions Taken, Coaching
										Given):
									</Text>
									<Textarea
										fontSize={16}
										disabled={Boolean(submission)}
										_disabled={{
											opacity: 1,
											bgColor: theme.colors.gray[400],
											color: theme.colors.black
										}}
										bgColor={theme.colors.white}
										borderRadius="base"
										value={comments}
										onChange={({ target }) => dispatch(updateComments({ id, value: target.value }))}
									/>
								</Box>
							</Flex>
						</Accordion.ItemBody>
					</Accordion.ItemContent>
				</Accordion.Item>
				<Accordion.Item value={3} mb={4} borderBottomWidth="1px">
					<Accordion.ItemTrigger>
						<Accordion.ItemIndicator />
						<Text fontSize={20} fontWeight="bold" color={theme.colors.blue[700]}>
							Violations
						</Text>
					</Accordion.ItemTrigger>
					<Accordion.ItemContent p={0}>
						<Accordion.ItemBody>
							<Flex flexDirection="row" alignItems="center" justifyContent="flex-start" wrap="wrap">
								<Box
									flex={1}
									flexBasis="48%"
									p="8px"
									m="8px"
									bgColor={theme.colors.gray[500]}
									borderRadius="base"
								>
									<Text fontSize={16} as="label" pb="4px">
										Violations (any deviations from regulatory standards, accepted practices,
										company policies/procedures or anything that leads or could lead to a near-miss,
										accident, injury, property damage and/or death):
									</Text>
									<Flex flexDirection="row" pb="8px">
										<Flex flex={3} alignItems="center" justifyContent="center" mr="8px">
											<Text fontSize={16} as="label" pb="4px">
												Description
											</Text>
										</Flex>
										<Flex flex={1} alignItems="center" justifyContent="center">
											<Text fontSize={16} as="label" pb="4px" mr="8px">
												Major / Moderate / Minor
											</Text>
										</Flex>
										<Box w="32px" />
									</Flex>
									{violations.map((violation, index) => (
										<Flex key={index} flexDirection="row" alignItems="center" pb="8px">
											<Box w="28px">
												<Text fontSize={16}>{index + 1}. </Text>
											</Box>
											<Field.Root invalid={!violation.description}>
												<Input
													fontSize={16}
													disabled={Boolean(submission)}
													_disabled={{
														opacity: 1,
														bgColor: theme.colors.gray[400],
														color: theme.colors.black
													}}
													flex={3}
													mr="8px"
													bgColor={theme.colors.white}
													borderRadius="base"
													value={violation.description}
													onChange={({ target }) =>
														dispatch(
															updateViolationsField({
																id,
																index,
																key: 'description',
																value: target.value
															})
														)
													}
												/>
											</Field.Root>
											<NativeSelect.Root size="sm">
												<NativeSelect.Field
													fontSize={16}
													disabled={Boolean(submission)}
													_disabled={{
														opacity: 1,
														bgColor: theme.colors.gray[400],
														color: theme.colors.black
													}}
													flex={1}
													mr="8px"
													bgColor={theme.colors.white}
													placeholder=" "
													invalid={!violation.majorModerateMinor}
													onChange={({ target }) =>
														dispatch(
															updateViolationsField({
																id,
																index,
																key: 'majorModerateMinor',
																value: target.value
															})
														)
													}
													value={violation.majorModerateMinor ?? ''}
													borderRadius="base"
												>
													{MAJOR_MODERATE_MINOR_SELECTIONS.map((selection, index) => (
														<option key={`${selection}_${index}`} value={selection}>
															{selection}
														</option>
													))}
												</NativeSelect.Field>
												<NativeSelect.Indicator />
											</NativeSelect.Root>
											<Box w="32px" />
										</Flex>
									))}
								</Box>
							</Flex>
						</Accordion.ItemBody>
					</Accordion.ItemContent>
				</Accordion.Item>
			</Accordion.Root>
		</Box>
	);
};
